import React, { FC } from 'react';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import { WearableDataUtils } from '../../../common/utils/services/wearable-data-utils';
import { useTranslation } from 'react-i18next';

interface OvProviderSourceBadgeProps {
  provider?: string;
  compact?: boolean;
  className?: string;
}

const OvProviderSourceBadge: FC<OvProviderSourceBadgeProps> = ({
  provider,
  compact = false,
  className,
}) => {
  const { t } = useTranslation();
  const providerName = WearableDataUtils.getProviderDisplayName(provider);

  if (!providerName) {
    return null;
  }

  const label = t('common.wearable.syncedFrom', { provider: providerName });
  const logoSrc = WearableDataUtils.getProviderLogoSrc(provider);

  return (
    <Badge
      className={className}
      title={label}
      aria-label={label}
      $compact={compact}
    >
      {logoSrc ? <Logo src={logoSrc} alt="" aria-hidden="true" /> : null}
      {!compact && <Label>{label}</Label>}
      {compact && <ScreenReaderLabel>{label}</ScreenReaderLabel>}
    </Badge>
  );
};

export default OvProviderSourceBadge;

const Badge = styled.span<{ $compact: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-width: ${(props) => (props.$compact ? '1.75rem' : '0')};
  max-width: 100%;
  min-height: 1.5rem;
  padding: ${(props) => (props.$compact ? '0.25rem' : '0.25rem 0.5rem')};
  border: 1px solid ${Colours.OV_BASE};
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background-color: ${Colours.LIGHT_GREEN};
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
  line-height: 1;
  white-space: nowrap;
  vertical-align: middle;
`;

const Logo = styled.img`
  width: 0.875rem;
  height: 0.875rem;
  object-fit: contain;
  flex: 0 0 auto;
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScreenReaderLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
